import type { FeatureGate, OverrideAdapter, StatsigUser } from '@statsig/react-bindings'

import type { StatsigGateId } from '~/configs/statsig'

import { GATE_LABELS } from './gateLabels'

/**
 * Local gate overrides for the Synop-internal feature flag panel.
 *
 * Statsig evaluates gates once per user and hands the client an immutable
 * payload. Rather than mutate that payload, this module implements the SDK's
 * {@link OverrideAdapter} contract: `getGateOverride` runs on every gate
 * evaluation and can substitute a value before it reaches the caller. Statsig's
 * own evaluation is never touched, which is what makes "reset to the values
 * this user actually has" a matter of dropping the override rather than
 * restoring a snapshot — a snapshot would go stale the moment Statsig refetches
 * or the user switches org.
 *
 * Overrides are client-side only. Services that evaluate the same gate
 * server-side (reports-api reads `eng-9535_includedeleted_flag`) are unaffected.
 */

/** Survives reload — some gates only take effect on a fresh mount — but not a
 *  browser restart, so a forgotten override cannot outlive the session and
 *  present as a broken app days later. */
const STORAGE_KEY = 'synop_gate_overrides'

export type GateOverrides = Partial<Record<StatsigGateId, boolean>>

/**
 * Gates this module refuses to override, enforced here rather than in the panel
 * because `sessionStorage` is user-writable and the adapter runs for every
 * Statsig client.
 *
 * `synop_internal_only` decides who reaches the panel at all, so forcing it off
 * would hide the control needed to undo that, and forcing it on would paint
 * staff-only surfaces for someone the backend never granted them to. Overriding
 * it grants no data access — every endpoint still authorizes off the JWT — but
 * a UI that disagrees with the server is its own kind of broken.
 */
export const UNOVERRIDABLE_GATES: ReadonlySet<StatsigGateId> = new Set<StatsigGateId>(['synop_internal_only'])

/**
 * The placeholder the Statsig providers evaluate under before Auth0 resolves
 * the signed-in user, and for the whole unauthenticated tree. It identifies
 * nobody, so ownership checks must ignore it — treating it as a real identity
 * would drop overrides on every page load, which is exactly what persisting
 * them is meant to survive.
 */
export const ANONYMOUS_STATSIG_USER_ID = 'unknown_user'

type PersistedOverrides = {
  /** Statsig `userID` that set these overrides. */
  ownerId: string | null
  gates: GateOverrides
}

function sanitizeGates(value: unknown): GateOverrides {
  if (!value || typeof value !== 'object') return {}
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).filter(
      // Keys must exist in the gate registry: sessionStorage can hold an
      // override for a gate a deploy has since retired, which would count
      // toward "Overridden" while the panel renders no row to clear it.
      ([gateId, gateValue]) =>
        typeof gateValue === 'boolean' &&
        gateId in GATE_LABELS &&
        !UNOVERRIDABLE_GATES.has(gateId as StatsigGateId)
    )
  ) as GateOverrides
}

function readFromStorage(): PersistedOverrides {
  const empty: PersistedOverrides = { ownerId: null, gates: {} }
  if (typeof window === 'undefined') return empty
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return empty
    const parsed: unknown = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return empty
    const record = parsed as Record<string, unknown>
    return {
      ownerId: typeof record.ownerId === 'string' ? record.ownerId : null,
      gates: sanitizeGates(record.gates)
    }
  } catch {
    return empty
  }
}

function writeToStorage() {
  if (typeof window === 'undefined') return
  try {
    if (Object.keys(overrides).length === 0) window.sessionStorage.removeItem(STORAGE_KEY)
    else window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ownerId, gates: overrides }))
  } catch {
    // sessionStorage unavailable (private mode, quota) — overrides stay in memory
  }
}

/**
 * Notify subscribers on a microtask rather than inline.
 *
 * Gates are evaluated while components render — `getGateOverride` runs inside
 * that pass — so any store write reachable from an evaluation would otherwise
 * set state during another component's render. Deferring also coalesces a whole
 * render pass into a single notification.
 */
function createDeferredNotifier(listeners: ReadonlySet<() => void>): () => void {
  let scheduled = false
  return () => {
    if (scheduled) return
    scheduled = true
    queueMicrotask(() => {
      scheduled = false
      listeners.forEach((listener) => listener())
    })
  }
}

const persisted = readFromStorage()
let overrides: GateOverrides = persisted.gates
let ownerId: string | null = persisted.ownerId
const listeners = new Set<() => void>()
const notifyOverrideListeners = createDeferredNotifier(listeners)

/**
 * Drop overrides that belong to a different signed-in user.
 *
 * Overrides live in `sessionStorage` and the adapter is installed for every
 * Statsig client, so without this a staff member who forces a few gates and
 * then signs out leaves them applied to whoever signs in next in that tab —
 * someone with no way to reach the panel and undo it. Ownership is settled
 * here, at the point a value would be substituted, against the identity the
 * SDK is actually evaluating for.
 */
function reconcileOwner(userId: string | undefined) {
  if (!userId || userId === ANONYMOUS_STATSIG_USER_ID) return
  if (ownerId === userId) return
  const inherited = ownerId !== null
  ownerId = userId
  if (inherited && Object.keys(overrides).length > 0) commit({})
  else writeToStorage()
}

/**
 * Replaced wholesale on every write so the reference doubles as the
 * `useSyncExternalStore` snapshot.
 */
function commit(next: GateOverrides) {
  overrides = next
  writeToStorage()
  notifyOverrideListeners()
}

export function getGateOverrides(): GateOverrides {
  return overrides
}

export function subscribeToGateOverrides(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** `null` drops the override and restores whatever Statsig evaluated. */
export function setGateOverride(gateId: StatsigGateId, value: boolean | null) {
  if (UNOVERRIDABLE_GATES.has(gateId)) return
  const next = { ...overrides }
  if (value === null) delete next[gateId]
  else next[gateId] = value
  commit(next)
}

export function clearGateOverrides() {
  if (Object.keys(overrides).length === 0) return
  commit({})
}

/**
 * Gate IDs the app has evaluated on the route currently being viewed.
 *
 * The SDK routes every `checkGate`/`getFeatureGate` through the override
 * adapter before returning, so the adapter doubles as an accurate record of
 * which gates a screen actually read — no static route-to-gate map to maintain
 * and nothing to keep in sync as components move.
 *
 * The reset is lazy rather than driven by a route effect: gate evaluation
 * happens while page components render, which is *before* any effect could
 * fire, so an effect-based reset would wipe the very gates it meant to scope.
 * Comparing the live pathname at record time avoids that ordering problem
 * entirely.
 */
let screenPath: string | null = null
let screenGates: ReadonlySet<StatsigGateId> = new Set()
const screenListeners = new Set<() => void>()
const notifyScreenListeners = createDeferredNotifier(screenListeners)

function currentPath(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.pathname
}

function recordScreenEvaluation(gateId: StatsigGateId) {
  const path = currentPath()
  if (path !== screenPath) {
    screenPath = path
    screenGates = new Set([gateId])
    notifyScreenListeners()
    return
  }
  if (screenGates.has(gateId)) return
  screenGates = new Set(screenGates).add(gateId)
  notifyScreenListeners()
}

/**
 * Drop the record when the route changed without evaluating a single gate,
 * which `recordScreenEvaluation` alone would never notice. Idempotent, so it is
 * safe to call on every render.
 */
export function syncScreenPath(path: string) {
  if (path === screenPath) return
  screenPath = path
  screenGates = new Set()
  notifyScreenListeners()
}

export function subscribeToScreenGates(listener: () => void): () => void {
  screenListeners.add(listener)
  return () => {
    screenListeners.delete(listener)
  }
}

export function getScreenGates(): ReadonlySet<StatsigGateId> {
  return screenGates
}

let bypassOverrides = false

/**
 * Evaluate gates as if nothing were overridden, so the panel can show the value
 * Statsig actually returned alongside the value being forced. Reading it through
 * `checkGate` would otherwise pass back through the adapter and echo the
 * override, hiding the very baseline the Default control restores.
 *
 * Marks the read as introspection rather than the app asking for a gate value,
 * so the panel listing every gate does not report all of them as belonging
 * to the current screen.
 *
 * Synchronous by design: the flag is only ever set for the duration of `read`.
 */
export function withOverridesBypassed<T>(read: () => T): T {
  bypassOverrides = true
  try {
    return read()
  } finally {
    bypassOverrides = false
  }
}

export const gateOverrideAdapter: OverrideAdapter = {
  getGateOverride(current: FeatureGate, user: StatsigUser): FeatureGate | null {
    if (bypassOverrides) return null
    reconcileOwner(user?.userID)
    const gateId = current.name as StatsigGateId
    recordScreenEvaluation(gateId)
    const value = overrides[gateId]
    if (value === undefined) return null
    if (value === current.value) {
      // Statsig now resolves to the forced value, so the override changes
      // nothing. Drop it rather than leave it pinning this gate against a
      // later change, and so the overridden count only ever counts deviations.
      const next = { ...overrides }
      delete next[gateId]
      commit(next)
      return null
    }
    return { ...current, value, details: { ...current.details, reason: 'LocalOverride' } }
  }
}
